/**
 * Two things the renderer needs are known only to the live document, and serialising
 * `innerHTML` throws both away: the colour a stylesheet gives a syntax token, and the width
 * the browser's auto table layout settled on. Both are stamped onto a clone as inline values
 * the mapper can read back (SNAP-2257).
 */

/** Syntax colours live in `CodeBlockLowlight.scss` as `.hljs-*` class rules. */
const stampCodeColours = (live: Element, clone: Element) => {
  const from = live.querySelectorAll('pre span');
  const to = clone.querySelectorAll('pre span');

  from.forEach((el, index) => {
    const target = to[index] as HTMLElement | undefined;
    if (!target) return;
    target.style.color = getComputedStyle(el).color;
  });
};

/**
 * A read-only table is `display: contents` with `max-width: fit-content`, so its columns are
 * content-sized and the table need not fill the line. The widths go across in CSS pixels: a
 * page is narrower than the editor, and a share of it would squeeze a column that fits.
 */
const stampTableWidths = (live: Element, clone: Element) => {
  const from = live.querySelectorAll('table');
  const to = clone.querySelectorAll('table');

  from.forEach((table, index) => {
    const target = to[index] as HTMLElement | undefined;
    if (!target) return;

    // The widest row defines the grid; a spanning cell contributes its share to each column
    // it covers, so the list always has one entry per column.
    const rows = Array.from(table.querySelectorAll('tr'));
    const span = (cell: Element) => Number(cell.getAttribute('colspan')) || 1;
    const widest = rows.reduce<Element | null>((best, row) => {
      const columns = Array.from(row.children).reduce((sum, cell) => sum + span(cell), 0);
      const bestColumns = best ? Array.from(best.children).reduce((sum, cell) => sum + span(cell), 0) : 0;
      return columns > bestColumns ? row : best;
    }, null);
    if (!widest) return;

    const widths = Array.from(widest.children).flatMap((cell) => {
      const each = cell.getBoundingClientRect().width / span(cell);
      return Array.from({ length: span(cell) }, () => each);
    });
    if (widths.some((width) => !width)) return;

    target.setAttribute('data-pdf-columns', widths.map((width) => width.toFixed(2)).join(','));
  });
};

/**
 * The Image node view wraps every picture in `<span class="image-view__wrapper">` around a
 * `<div>`, and a `<div>` may not live inside a `<p>`. This clone is serialised and re-parsed,
 * and the parser evicts that div, splitting one paragraph of inline images into a block each,
 * so pictures the editor shows side by side came out stacked. Unwrapping to the bare `<img>`
 * is also exactly what the stored HTML holds, so both sources reach the mapper alike
 * (SNAP-2257).
 */
const unwrapImageViews = (clone: Element) => {
  clone.querySelectorAll('.image-view__wrapper').forEach((wrapper) => {
    const img = wrapper.querySelector('img');
    if (img) wrapper.replaceWith(img);
  });
};

export const annotatePainted = (content: Element): string => {
  const clone = content.cloneNode(true) as HTMLElement;

  stampCodeColours(content, clone);
  stampTableWidths(content, clone);
  unwrapImageViews(clone);

  return clone.innerHTML;
};

export default annotatePainted;
